import { useQuery } from '@tanstack/react-query';

import { Currency } from '@/constants';
import { BtcLikeCurrency } from '@/types';
import { convertBigIntToString } from '@/utils';

import { ResultUtxo } from '../common/transactions';

import { useBtcLikeWalletUtxos } from './useBtcLikeWalletUtxos';

const INITIAL_BALANCE = {
  human: '0',
  raw: 0n,
  details: {
    confirmed: 0n,
    unconfirmed: 0n,
    total: 0n,
  },
};

export type BtcLikeWalletBalance = {
  human: string;
  raw: bigint;
  details: BtcLikeWalletUtxosValue;
};

export type BtcLikeWalletUtxosValue = {
  confirmed: bigint;
  unconfirmed: bigint;
  total: bigint;
};

export const useBtcLikeBalance = (token: BtcLikeCurrency, address: string | null) => {
  const { data: utxos } = useBtcLikeWalletUtxos(token, address);

  const queryKey = ['BtcLikeBalance', token, address ?? '', utxos];

  const queryFn = (): BtcLikeWalletBalance => {
    if (!utxos) return INITIAL_BALANCE;

    const utxoAmountsBreakdown = utxos.reduce(
      (acc: BtcLikeWalletUtxosValue, utxo: ResultUtxo) => {
        if (utxo.status.confirmed) acc.confirmed += BigInt(utxo.value);
        else acc.unconfirmed += BigInt(utxo.value);

        acc.total += BigInt(utxo.value);

        return acc;
      },
      { confirmed: 0n, unconfirmed: 0n, total: 0n }
    );

    return {
      human: convertBigIntToString(utxoAmountsBreakdown.total, Currency.BTC),
      raw: utxoAmountsBreakdown.total,
      details: utxoAmountsBreakdown,
    };
  };

  return useQuery({
    queryKey,
    queryFn,
    initialData: INITIAL_BALANCE,
    enabled: !!address,
    refetchInterval: 10 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};
